import { create } from "zustand";
import { persist } from "zustand/middleware";
import { useMockStore } from "./store";
import { mockProjectClientContacts } from "./seed";
import { getStoredImpersonation } from "@/os/impersonation/ImpersonationProvider";
import type {
  TaskFull, TaskAssignee, TaskSubtask, TaskComment, TaskAttachment, TaskReaction,
  CandidateAssignee, TaskStatus,
} from "../tasksApi";

/* ============================================================================
   Sandbox-only persisted store for the rich Tasks system.
   Seeded from `mockTasks` + `mockProjectTeam` + `mockTeamProfiles` on first use.
   ============================================================================ */

export const MOCK_CURRENT_USER_ID = "mock-current-user";
const MOCK_CURRENT_USER_NAME = "Tú";

const newId = () => crypto.randomUUID();
const nowISO = () => new Date().toISOString();

const mapStatus = (s: string): TaskStatus => {
  if (s === "completed" || s === "done") return "done";
  if (s === "uat") return "uat";
  if (s === "in_progress") return "in_progress";
  return "todo";
};

type RichState = {
  seeded: boolean;
  tasks: TaskFull[];
  comments: TaskComment[];
  attachments: TaskAttachment[];
  seed: () => void;
  reset: () => void;
};

const profileFor = (tpId: string) => {
  const tp = useMockStore.getState().teamProfiles.find((p) => p.id === tpId);
  if (!tp) return null;
  return {
    full_name: tp.display_name,
    email: tp.email,
    avatar_url: tp.avatar_url,
  };
};

const mockActor = () => {
  const active = getStoredImpersonation();
  if (active) {
    return {
      id: active.user_id ?? active.id,
      profile: {
        full_name: active.name,
        email: active.email,
        avatar_url: active.avatar_url,
      },
    };
  }
  return {
    id: MOCK_CURRENT_USER_ID,
    profile: { full_name: MOCK_CURRENT_USER_NAME, email: null, avatar_url: null },
  };
};

function buildSeed(): { tasks: TaskFull[]; comments: TaskComment[]; attachments: TaskAttachment[] } {
  const st = useMockStore.getState();
  const tasks: TaskFull[] = st.tasks.map((t, idx) => {
    // Round-robin across the FULL project team so every member ends up with
    // active + completed tasks (rather than only the first one or two).
    const fullTeam = st.projectTeam
      .filter((pt) => pt.project_id === t.project_id)
      .sort((a, b) => a.order_index - b.order_index);
    const count = fullTeam.length === 0 ? 0 : 1 + (idx % 2); // 1 or 2 assignees
    const team: typeof fullTeam = [];
    for (let i = 0; i < count && fullTeam.length > 0; i++) {
      team.push(fullTeam[(idx + i) % fullTeam.length]);
    }
    const assignees: TaskAssignee[] = team.map((m) => ({
      user_id: m.team_profile_id,
      assigned_by: MOCK_CURRENT_USER_ID,
      assigned_at: t.created_at,
      profile: profileFor(m.team_profile_id),
    }));
    // Attach to first milestone of the project ~half the time
    const ms = st.milestones.find((m) => m.project_id === t.project_id);
    const milestoneId = ms && idx % 2 === 0 ? ms.id : null;

    return {
      id: t.id,
      project_id: t.project_id,
      milestone_id: milestoneId,
      title: t.title,
      description: t.description ?? null,
      status: mapStatus(t.status),
      created_by: MOCK_CURRENT_USER_ID,
      created_at: t.created_at,
      deadline_at: (t as any).due_date ?? null,
      completed_at: t.status === "completed" ? t.created_at : null,
      last_assigned_at: assignees.length ? t.created_at : null,
      assignees,
      subtasks: [],
      comments_count: 0,
      attachments_count: 0,
      reactions: [],
    };
  });
  // Guarantee the sandbox operator persona (Aleksei / tp-116) ends up with
  // a healthy mix of active + completed tasks across every project they are
  // part of, so the Work Studio "Tus tareas asignadas" widget is never empty.
  const SANDBOX_OP_TP = "tp-116";
  const opProjects = new Set(
    st.projectTeam.filter((m) => m.team_profile_id === SANDBOX_OP_TP).map((m) => m.project_id),
  );
  for (const pid of opProjects) {
    const projectTasks = tasks.filter((t) => t.project_id === pid);
    const active = projectTasks.filter((t) => t.status !== "done");
    const have = active.filter((t) => t.assignees.some((a) => a.user_id === SANDBOX_OP_TP));
    const target = Math.min(3, active.length);
    if (have.length >= target) continue;
    const needed = target - have.length;
    const candidates = active.filter((t) => !t.assignees.some((a) => a.user_id === SANDBOX_OP_TP));
    for (let i = 0; i < needed && i < candidates.length; i++) {
      const t = candidates[i];
      t.assignees = [
        ...t.assignees,
        {
          user_id: SANDBOX_OP_TP,
          assigned_by: MOCK_CURRENT_USER_ID,
          assigned_at: t.created_at,
          profile: profileFor(SANDBOX_OP_TP),
        },
      ];
      t.last_assigned_at = t.created_at;
    }
  }
  return { tasks, comments: [], attachments: [] };
}

export const useTasksRichStore = create<RichState>()(
  persist(
    (set, get) => ({
      seeded: false,
      tasks: [],
      comments: [],
      attachments: [],
      seed: () => {
        if (get().seeded) return;
        const s = buildSeed();
        set({ ...s, seeded: true });
      },
      reset: () => {
        const s = buildSeed();
        set({ ...s, seeded: true });
      },
    }),
    { name: "sc-os-tasks-rich-v8" },
  ),
);

export const ensureTasksRichSeeded = () => {
  const st = useTasksRichStore.getState();
  // Reseed if not seeded yet, if persisted state is empty, OR if the persisted
  // task IDs no longer align with the in-memory mock store (mock store is NOT
  // persisted, so project/task IDs regenerate on every reload).
  if (!st.seeded || st.tasks.length === 0) { st.reset(); return; }
  const liveProjectIds = new Set(useMockStore.getState().projects.map((p) => p.id));
  const stale = !st.tasks.some((t) => liveProjectIds.has(t.project_id));
  if (stale) st.reset();
};

const setTasks = (fn: (tasks: TaskFull[]) => TaskFull[]) =>
  useTasksRichStore.setState((s) => ({ tasks: fn(s.tasks) }));
const setComments = (fn: (c: TaskComment[]) => TaskComment[]) =>
  useTasksRichStore.setState((s) => ({ comments: fn(s.comments) }));
const setAttachments = (fn: (a: TaskAttachment[]) => TaskAttachment[]) =>
  useTasksRichStore.setState((s) => ({ attachments: fn(s.attachments) }));

/* Helpers to keep counts in sync */
const recountTask = (taskId: string) =>
  setTasks((tasks) => tasks.map((t) => {
    if (t.id !== taskId) return t;
    const cc = useTasksRichStore.getState().comments.filter((c) => c.task_id === taskId).length;
    const ac = useTasksRichStore.getState().attachments.filter((a) => a.task_id === taskId).length;
    return { ...t, comments_count: cc, attachments_count: ac };
  }));

/* ===== API ===== */

export async function mockListTasks(projectId: string): Promise<TaskFull[]> {
  ensureTasksRichSeeded();
  return useTasksRichStore.getState().tasks
    .filter((t) => t.project_id === projectId)
    .sort((a, b) => b.created_at.localeCompare(a.created_at));
}

export async function mockListMilestonesLite(projectId: string) {
  return useMockStore.getState().milestones
    .filter((m) => m.project_id === projectId)
    .map((m) => ({ id: m.id, title: m.title }));
}

export async function mockListCandidateAssignees(projectId: string): Promise<CandidateAssignee[]> {
  const st = useMockStore.getState();
  const team = st.projectTeam.filter((m) => m.project_id === projectId);
  const profiles: CandidateAssignee[] = team
    .map((m) => {
      const tp = st.teamProfiles.find((p) => p.id === m.team_profile_id);
      if (!tp) return null;
      return {
        user_id: tp.id,
        full_name: tp.display_name,
        email: tp.email,
        avatar_url: tp.avatar_url,
        role_hint: "operator" as const,
        headline: tp.headline ?? null,
        role_on_project: m.role_on_project ?? null,
      };
    })
    .filter(Boolean) as CandidateAssignee[];

  // Add the current sandbox user as "admin" so they can self-assign.
  profiles.unshift({
    user_id: MOCK_CURRENT_USER_ID,
    full_name: MOCK_CURRENT_USER_NAME,
    email: null,
    avatar_url: null,
    role_hint: "admin",
  });

  // Client team — include all active (non-disabled) contacts from the
  // project's client team (seeded contacts + accepted invitations).
  const overrides = st.clientContactOverrides;
  const seeded = mockProjectClientContacts[projectId] ?? [];
  seeded.forEach((c, i) => {
    const cid = c.id ?? `${projectId}-cc-${i}`;
    const o = overrides[cid];
    if (o?.removed) return;
    const status = o?.status ?? null;
    if (status === "disabled" || status === "pending") return;
    profiles.push({
      user_id: `mock-client-contact-${cid}`,
      full_name: o?.display_name ?? c.display_name,
      email: (o?.email ?? c.email) ?? null,
      avatar_url: c.avatar_url ?? null,
      role_hint: "client",
    });
  });
  st.projectInvitations
    .filter((i) => i.project_id === projectId && i.status === "active")
    .forEach((i) => {
      profiles.push({
        user_id: `mock-invite-${i.id}`,
        full_name: i.name,
        email: i.email,
        avatar_url: null,
        role_hint: "client",
      });
    });
  return profiles;
}

export async function mockListTaskComments(taskId: string): Promise<TaskComment[]> {
  return useTasksRichStore.getState().comments
    .filter((c) => c.task_id === taskId)
    .sort((a, b) => a.created_at.localeCompare(b.created_at));
}

export async function mockListTaskAttachments(taskId: string): Promise<TaskAttachment[]> {
  return useTasksRichStore.getState().attachments
    .filter((a) => a.task_id === taskId)
    .sort((a, b) => b.created_at.localeCompare(a.created_at));
}

export async function mockCreateTask(p: {
  project_id: string; title: string; description?: string;
  milestone_id?: string | null; deadline_at?: string | null; assignee_ids?: string[];
}): Promise<string> {
  ensureTasksRichSeeded();
  const actor = mockActor();
  const id = newId();
  const created_at = nowISO();
  const assignees: TaskAssignee[] = (p.assignee_ids ?? []).map((uid) => ({
    user_id: uid,
    assigned_by: actor.id,
    assigned_at: created_at,
    profile: profileFor(uid) ?? null,
  }));
  const task: TaskFull = {
    id, project_id: p.project_id, milestone_id: p.milestone_id ?? null,
    title: p.title, description: p.description ?? null,
    status: "todo", created_by: actor.id, created_at,
    deadline_at: p.deadline_at ?? null, completed_at: null,
    last_assigned_at: assignees.length ? created_at : null,
    assignees, subtasks: [], comments_count: 0, attachments_count: 0, reactions: [],
  };
  setTasks((t) => [task, ...t]);
  return id;
}

export async function mockUpdateTask(id: string, patch: Partial<TaskFull>) {
  setTasks((tasks) => tasks.map((t) => {
    if (t.id !== id) return t;
    const next: TaskFull = { ...t, ...patch } as TaskFull;
    if (patch.status === "done" && !t.completed_at) next.completed_at = nowISO();
    if (patch.status && patch.status !== "done") next.completed_at = null;
    return next;
  }));
}

export async function mockDeleteTask(id: string) {
  setTasks((tasks) => tasks.filter((t) => t.id !== id));
  setComments((c) => c.filter((x) => x.task_id !== id));
  setAttachments((a) => a.filter((x) => x.task_id !== id));
}

export async function mockSetAssignees(taskId: string, userIds: string[]) {
  const actor = mockActor();
  const at = nowISO();
  setTasks((tasks) => tasks.map((t) => {
    if (t.id !== taskId) return t;
    const prev = new Map(t.assignees.map((a) => [a.user_id, a] as const));
    const next: TaskAssignee[] = userIds.map((uid) => prev.get(uid) ?? {
      user_id: uid,
      assigned_by: actor.id,
      assigned_at: at,
      profile: profileFor(uid) ?? null,
    });
    const changed = next.length !== t.assignees.length
      || next.some((a) => !prev.has(a.user_id));
    return { ...t, assignees: next, last_assigned_at: changed ? at : t.last_assigned_at };
  }));
}

/* Subtasks */
export async function mockAddSubtask(taskId: string, title: string, order_index = 0) {
  const actor = mockActor();
  const sub: TaskSubtask = {
    id: newId(), task_id: taskId, title, done: false, order_index,
    created_at: nowISO(), created_by: actor.id,
  };
  setTasks((tasks) => tasks.map((t) =>
    t.id === taskId ? { ...t, subtasks: [...t.subtasks, sub] } : t));
}
export async function mockToggleSubtask(id: string, done: boolean) {
  setTasks((tasks) => tasks.map((t) => ({
    ...t, subtasks: t.subtasks.map((s) => s.id === id ? { ...s, done } : s),
  })));
}
export async function mockUpdateSubtask(id: string, patch: Partial<TaskSubtask>) {
  setTasks((tasks) => tasks.map((t) => ({
    ...t, subtasks: t.subtasks.map((s) => s.id === id ? { ...s, ...patch } : s),
  })));
}
export async function mockDeleteSubtask(id: string) {
  setTasks((tasks) => tasks.map((t) => ({
    ...t, subtasks: t.subtasks.filter((s) => s.id !== id),
  })));
}

/* Comments */
export async function mockAddComment(taskId: string, body: string) {
  const actor = mockActor();
  const c: TaskComment = {
    id: newId(), task_id: taskId, author_id: actor.id, body,
    created_at: nowISO(),
    author: actor.profile,
    reactions: [],
  };
  setComments((cs) => [...cs, c]);
  recountTask(taskId);
}
export async function mockDeleteComment(id: string) {
  const c = useTasksRichStore.getState().comments.find((x) => x.id === id);
  setComments((cs) => cs.filter((x) => x.id !== id));
  if (c) recountTask(c.task_id);
}

/* Reactions — toggle */
export async function mockToggleReaction(target: { task_id?: string; comment_id?: string }, emoji: string) {
  const uid = mockActor().id;
  if (target.task_id) {
    const tid = target.task_id;
    setTasks((tasks) => tasks.map((t) => {
      if (t.id !== tid) return t;
      const has = t.reactions.find((r) => r.user_id === uid && r.emoji === emoji);
      const reactions = has
        ? t.reactions.filter((r) => r !== has)
        : [...t.reactions, { id: newId(), user_id: uid, emoji, task_id: tid, comment_id: null }];
      return { ...t, reactions };
    }));
    return;
  }
  if (target.comment_id) {
    const cid = target.comment_id;
    setComments((cs) => cs.map((c) => {
      if (c.id !== cid) return c;
      const has = c.reactions.find((r) => r.user_id === uid && r.emoji === emoji);
      const reactions = has
        ? c.reactions.filter((r) => r !== has)
        : [...c.reactions, { id: newId(), user_id: uid, emoji, task_id: null, comment_id: cid }];
      return { ...c, reactions };
    }));
  }
}

/* Attachments — store as data URL */
function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}
export async function mockUploadAttachment(taskId: string, file: File) {
  const actor = mockActor();
  const url = await readAsDataUrl(file);
  const att: TaskAttachment = {
    id: newId(), task_id: taskId, uploader_id: actor.id,
    name: file.name, storage_path: `mock://${taskId}/${file.name}`,
    url, mime_type: file.type, size_bytes: file.size,
    created_at: nowISO(),
  };
  setAttachments((a) => [att, ...a]);
  recountTask(taskId);
}
export async function mockDeleteAttachment(att: TaskAttachment) {
  setAttachments((a) => a.filter((x) => x.id !== att.id));
  recountTask(att.task_id);
}
export async function mockRefreshAttachmentUrl(att: TaskAttachment) {
  return att.url ?? null;
}